import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  checkSessionStatus,
  fetchUserData,
  logoutUser,
  loginUser,
  loginWithAccessCode,
  LoginParams,
  LoginWithAccessCodeParams,
  UserData
} from "./api";
import mySessionStorage from "../../controllers/sessionStorage.controllers";

export const useCheckSession = () => {
  return useQuery({
    queryKey: ["auth", "session"],
    queryFn: checkSessionStatus,
    staleTime: Infinity,
    retry: false
  });
};

export const useUserData = (enabled: boolean = true) => {
  const queryResult = useQuery<UserData | null, Error, UserData | null>({
    queryKey: ["auth", "userData"],
    queryFn: fetchUserData,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false
  });

  useEffect(() => {
    if (queryResult.data) {
      const isSessionADAC = queryResult.data.user?.type === "ADAC";
      const currentValue = mySessionStorage.get("sessionADAC");
      if (currentValue !== true) {
        mySessionStorage.set("sessionADAC", isSessionADAC);
      }
    }
  }, [queryResult.data]);

  return queryResult;
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logoutUser,
    onSuccess: () => {
      queryClient.setQueryData(["auth", "session"], false);
      queryClient.removeQueries({ queryKey: ["auth", "userData"] });
    }
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: LoginParams) => loginUser(params),
    onSuccess: data => {
      if (data.code) {
        queryClient.setQueryData(["auth", "session"], true);
        queryClient.invalidateQueries({ queryKey: ["auth", "userData"] });
      }
    }
  });
};

export const useLoginWithAccessCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: LoginWithAccessCodeParams) =>
      loginWithAccessCode(params),
    onSuccess: data => {
      if (data.code) {
        queryClient.invalidateQueries({ queryKey: ["auth"] });
      }
    }
  });
};
